import classNames from 'classnames';
import { h } from 'preact';

import { MoreIcon } from './Icons';
import { DraggableAttributes, PaymentButtons } from './PaymentButtons';
import { RadioOption } from './Radio';

export interface PaymentButtonsArrangerProps {
    buttons: RadioOption[];
    menu: RadioOption[];
    draggableButton: (option: RadioOption) => DraggableAttributes;
    draggableMenuItem: (option: RadioOption) => DraggableAttributes;
    // Dropped past the last button or menu item: to the end of that list.
    toButtons: DropTarget;
    toMenu: DropTarget;
    notice: string;
    lang: {
        buttons: string;
        menu: string;
        dragHere: string;
    };
}

interface DropTarget {
    onDragOver: (event: DragEvent) => void;
    onDrop: (event: DragEvent) => void;
}

/**
 * The checkout's payment buttons at the checkout's width, with its ⋯ menu opened beside them as a list, for the
 * cinema to arrange by dragging a method within or between the two.
 */
export const PaymentButtonsArranger = (props: PaymentButtonsArrangerProps): JSX.Element => (
    <div class="flex flex-wrap items-start gap-6">
        <div>
            <div class="mb-1.5 text-sm font-medium text-gray-700">{props.lang.buttons}</div>
            <div class="w-[360px] rounded-xl bg-gray-100 p-3">
                <div class="flex gap-2">
                    <div
                        class="flex min-h-[60px] min-w-0 grow"
                        onDragOver={props.toButtons.onDragOver}
                        onDrop={props.toButtons.onDrop}
                    >
                        {props.buttons.length > 0 ? (
                            <PaymentButtons options={props.buttons} draggable={props.draggableButton} />
                        ) : (
                            <span class="flex grow items-center justify-center px-2 text-center text-sm text-gray-500">
                                {props.lang.dragHere}
                            </span>
                        )}
                    </div>
                    <span class="btn btn-border shrink-0 bg-white px-2">
                        <MoreIcon class="w-6 h-6" />
                    </span>
                </div>
            </div>
            {props.notice && <p class="mt-1.5 max-w-[360px] text-sm text-danger-600">{props.notice}</p>}
        </div>

        <div>
            <div class="mb-1.5 text-sm font-medium text-gray-700">{props.lang.menu}</div>
            <ul
                class="min-h-[48px] w-[260px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow"
                onDragOver={props.toMenu.onDragOver}
                onDrop={props.toMenu.onDrop}
            >
                {props.menu.map((option) => {
                    const drag = props.draggableMenuItem(option);

                    return (
                        <li
                            key={option.value}
                            class={classNames(
                                'cursor-grab whitespace-nowrap px-4 py-2.5 hover:bg-gray-100',
                                drag.dragged && 'opacity-50'
                            )}
                            style={drag.style}
                            draggable={true}
                            onDragStart={drag.onDragStart}
                            onDragEnd={drag.onDragEnd}
                            onDragOver={drag.onDragOver}
                            onDrop={drag.onDrop}
                        >
                            {option.label}
                        </li>
                    );
                })}
                {props.menu.length === 0 && <li class="px-4 py-3 text-sm text-gray-500">{props.lang.dragHere}</li>}
            </ul>
        </div>
    </div>
);
